import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';

/**
 * Normalizes a SQL-style "YYYY-MM-DD HH:MM:SS" string (which browsers
 * otherwise parse as local time) to a Z-suffixed ISO string so it parses
 * as UTC. Pass-through if the string already has a timezone marker.
 */
function normalizeAsUtc(isoString) {
  if (!isoString) return '';
  const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(isoString);
  return hasTz ? isoString : isoString.replace(' ', 'T') + 'Z';
}

/**
 * Formats a UTC datetime string to relative time ("3m ago", "5h ago", "2d ago").
 * The source value is assumed to be UTC; we normalize before parsing so the
 * diff against now is computed correctly regardless of the browser's tz.
 * Falls back to locale date string for dates > 30 days.
 */
function relativeTimeUtc(isoString) {
  if (!isoString) return '\u2014';
  const then = new Date(normalizeAsUtc(isoString));
  if (Number.isNaN(then.getTime())) return '\u2014';
  const diffMs = Date.now() - then.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.toLocaleDateString();
}

/**
 * Full datetime for the popover on the UTC column \u2014 renders the UTC moment
 * in the user's local timezone (the browser's locale formatter handles the
 * conversion once we've parsed the source as UTC).
 */
function fullDateTimeUtc(isoString) {
  if (!isoString) return '';
  const d = new Date(normalizeAsUtc(isoString));
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleString();
}

/**
 * Formats a string that is already in the campus's LOCAL timezone \u2014 render
 * it as-is in MM/DD/YYYY h:mm AM/PM. We parse via UTC normalization and read
 * back with getUTC* accessors so the browser's own tz doesn't shift the
 * wall-clock value the campus actually saw.
 */
function formatLocalTimestamp(isoString) {
  if (!isoString) return '\u2014';
  const d = new Date(normalizeAsUtc(isoString));
  if (Number.isNaN(d.getTime())) return isoString;
  const pad = (n) => String(n).padStart(2, '0');
  const mm = pad(d.getUTCMonth() + 1);
  const dd = pad(d.getUTCDate());
  const yyyy = d.getUTCFullYear();
  let hh = d.getUTCHours();
  const min = pad(d.getUTCMinutes());
  const ampm = hh >= 12 ? 'PM' : 'AM';
  hh = hh % 12 || 12;
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
}

// ---------------------------------------------------------------------------
// Column definitions — 6 columns (removed alarm bytes raw/parsed from V1's 8)
// ---------------------------------------------------------------------------
export const ALL_SERVERS_COLUMN_DEFS = [
  { key: 'campus_name', label: 'Campus Name', sortable: true, filterable: true },
  { key: 'corporate_name', label: 'Corporation', sortable: true, filterable: true },
  { key: 'checkin_time', label: 'Checkin Time (UTC)', sortable: true, filterable: false },
  { key: 'recent_alarm_input_time', label: 'Recent Alarm Time (Local)', sortable: true, filterable: false },
  { key: 'version_database', label: 'DB Version', sortable: true, filterable: true },
  { key: 'version_code', label: 'Code Version', sortable: true, filterable: true },
];

// ---------------------------------------------------------------------------
// Status popover — wraps an element and shows detail on hover
// ---------------------------------------------------------------------------
const StatusPopover = ({ children, message }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const ref = useRef(null);
  const hoverTimeout = useRef(null);

  if (!message) return children;

  const handleEnter = () => {
    clearTimeout(hoverTimeout.current);
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popoverW = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    let left = centerX - popoverW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));

    setStyle({
      position: 'fixed',
      zIndex: 9999,
      left: `${left}px`,
      top: spaceBelow < 100 ? undefined : `${rect.bottom + 6}px`,
      bottom: spaceBelow < 100 ? `${window.innerHeight - rect.top + 6}px` : undefined,
      width: `${popoverW}px`,
      whiteSpace: 'normal',
    });
    setOpen(true);
  };

  const handleLeave = () => {
    hoverTimeout.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  useEffect(() => () => clearTimeout(hoverTimeout.current), []);

  return (
    <span ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave} style={{ position: 'relative', display: 'inline-block' }}>
      {children}
      {open ? (
        <span className="na-type-popover" style={style}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>{message}</span>
        </span>
      ) : null}
    </span>
  );
};

/**
 * AllServersTable — read-only data table for cross-campus PalCare servers
 * with sorting, filtering, and sticky header. No checkboxes or row actions.
 */
const AllServersTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
}) => {
  const activeColumns = ALL_SERVERS_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'campus_name':
        return row.campus_name && row.campus_id ? (
          <Link
            to={'/profiles/campus/' + row.campus_id}
            className="dp-device-link"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.campus_name}
          </Link>
        ) : (
          <span className="dp-cell-text" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
            {row.campus_name || '\u2014'}
          </span>
        );

      case 'corporate_name':
        return row.corporate_name && row.corporate_id ? (
          <Link
            to={'/profiles/corporate/' + row.corporate_id}
            className="dp-device-link"
          >
            {row.corporate_name}
          </Link>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'checkin_time':
        return row.checkin_time ? (
          <StatusPopover message={fullDateTimeUtc(row.checkin_time)}>
            <span className="dp-cell-text dp-cell-text--secondary">
              {relativeTimeUtc(row.checkin_time)}
            </span>
          </StatusPopover>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'recent_alarm_input_time':
        return row.recent_alarm_input_time ? (
          <span className="dp-cell-text dp-cell-text--secondary">
            {formatLocalTimestamp(row.recent_alarm_input_time)}
          </span>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th">
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row, idx) => (
            <tr key={`${row.campus_id}-${idx}`} className="dp-tr">
              {/* Data cells */}
              {activeColumns.map((col) => (
                <td key={col.key} className="dp-td">
                  {renderCell(row, col.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AllServersTable;
